import { useState, useEffect } from "react";
import {
  db,
  doc,
  updateDoc,
  functions,
  httpsCallable,
} from "../firebaseConfig";
import { DateTime } from "luxon";
import EmailModalV2 from "./EmailModalV2";
import { useAtom } from "jotai";
import { labelsAtom } from "../-Atoms";
import {
  numToLetter,
  parseDueDate,
  parseLineItems,
  parseTotal,
  makeBriefButtons,
  makeJobDetailsButton,
  getJobName,
} from "./outsourcerEmailUtils";

const OutsourcerEmailV2 = ({
  open,
  onClose,
  jobData,
  outsourcerJob,
  outsourcerIndex,
  outsourcerData,
}) => {
  const [labels] = useAtom(labelsAtom);
  const [emailTemplate, setEmailTemplate] = useState("");

  const sendEmail = httpsCallable(functions, "sendEmail");

  // Generate the email HTML template based on the 4 variants
  const generateEmailTemplate = () => {
    if (!jobData || !outsourcerData || !outsourcerJob) {
      return "";
    }

    const subject = getSubject();
    const hasRetouchingNotes = outsourcerJob.includeRetouchingNotes;
    const hasColourChanges = outsourcerJob.includeColourChanges;

    // Generate components (no need for theme-aware generation in utilities)
    const briefButtonsHtml = makeBriefButtons(outsourcerJob.briefs || []);
    const jobDetailsButton = makeJobDetailsButton(
      jobData,
      hasRetouchingNotes,
      hasColourChanges
    );
    const dueDate = parseDueDate(outsourcerJob.dueDate);
    const lineItems = parseLineItems(outsourcerJob.lineItems, outsourcerData);
    const total = parseTotal(outsourcerJob.lineItems);

    // Generate job details button HTML with CSS classes
    const jobDetailsButtonHtml = `<div style="text-align: center;">
<a href="${jobDetailsButton.url}" class="outsourcer-button" target="_blank">
  <span style="text-transform: uppercase;">${jobDetailsButton.label}</span>
</a>
</div>`;

    // Full HTML document with proper CSS media queries like Supply email
    let template = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <meta name="color-scheme" content="light dark" />
  <meta name="supported-color-schemes" content="light dark" />
  <style>
    /* Dark mode support */
    @media (prefers-color-scheme: dark) {
      body {
        background-color: #1a1a1a !important;
      }
      
      .email-wrapper {
        background-color: #1a1a1a !important;
      }
      
      .email-content {
        background-color: #2D2D2D !important;
        border-color: #444444 !important;
      }
      
      h1, h2, h3, p, div, pre {
        color: #ffffff !important;
      }
      
      .outsourcer-button {
        background-color: #000000 !important;
        color: #ffffff !important;
      }
      
      .instructions-text {
        color: #ffffff !important;
      }
    }
    
    /* Outlook dark mode support */
    
    [data-ogsc] .email-content {
      background-color: #2D2D2D !important;
      border-color: #444444 !important;
    }
    
    [data-ogsc] h1, [data-ogsc] h2, [data-ogsc] h3, [data-ogsc] p, [data-ogsc] div, [data-ogsc] pre {
      color: #ffffff !important;
    }
    
    [data-ogsc] .outsourcer-button {
      background-color: #000000 !important;
      color: #ffffff !important;
    }
    
    [data-ogsc] .instructions-text {
      color: #ffffff !important;
    }
    
    /* Default light mode styles */
    .outsourcer-button {
      background-color: #000000 !important;
      color: #ffffff !important;
      padding: 12px 20px;
      border-radius: 0.25rem;
      text-decoration: none;
      display: inline-block;
      font-size: 12px;
      font-weight: 600;
      text-align: center;
      min-width: 185px;
      line-height: 120%;
    }
  </style>
</head>
<body class="email-wrapper" style="background-color: #f3f4f6; font-family: Arial, Helvetica, sans-serif; margin: 0; padding: 0;">
  <div class="email-content" style="max-width: 700px; margin: 0 auto; background-color: #ffffff; padding: 2.5rem; border-radius: 0.75rem; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1); text-align: left; margin-top: 2.5rem; margin-bottom: 2.5rem;">

<p style="margin: 10px; color: #000000; font-size: 14px;">Hi ${outsourcerData.contactName},</p>

<p style="margin: 10px; color: #000000; font-size: 14px;">Our latest job is: <strong>${subject}</strong></p>

<p style="margin: 10px; color: #000000; font-size: 14px;">Please follow all instructions, these can be found either:</p>

<p style="margin: 10px; color: #000000; font-size: 14px;"><strong>Inside the job folder</strong></p>

<p style="margin: 10px; color: #000000; font-size: 14px;"><strong>Written on the images</strong></p>`;

    // Add conditional instruction text based on variant
    const highlightColor = "#F23620"; // Keep this red for emphasis

    if (hasRetouchingNotes && hasColourChanges) {
      template += `
<p style="margin: 10px; color: #000000; font-size: 14px;"><strong>In the retouching notes below</strong></p>

<p style="margin: 10px; color: ${highlightColor}; font-size: 14px;"><strong>Please see below the retouching notes for this job, check them off as you go along.</strong></p>

<p style="margin: 10px; color: ${highlightColor}; font-size: 14px;"><strong>If there is a little image icon next to the note, click it to open a reference image for that note.</strong></p>

<p style="margin: 10px; color: ${highlightColor}; font-size: 14px;"><strong>Please also see below the colour change notes for this job.</strong></p>`;
    } else if (hasRetouchingNotes && !hasColourChanges) {
      template += `
<p style="margin: 10px; color: #000000; font-size: 14px;"><strong>In the retouching notes below</strong></p>

<p style="margin: 10px; color: ${highlightColor}; font-size: 14px;"><strong>Please see below the retouching notes for this job, check them off as you go along.</strong></p>

<p style="margin: 10px; color: ${highlightColor}; font-size: 14px;"><strong>If there is a little image icon next to the note, click it to open a reference image for that note.</strong></p>`;
    } else if (!hasRetouchingNotes && hasColourChanges) {
      template += `
<p style="margin: 10px; color: #000000; font-size: 14px;"><strong>In the colour change notes below</strong></p>

<p style="margin: 10px; color: ${highlightColor}; font-size: 14px;"><strong>Please see below the colour change notes for this job.</strong></p>`;
    }

    // Add buttons and job details
    template += `

<br>
${jobDetailsButtonHtml}

${briefButtonsHtml ? `<br>${briefButtonsHtml}` : ""}

<p style="margin: 10px; color: #000000; font-size: 14px;"><strong>IMAGES:</strong></p>

<pre style="margin: 10px; color: #000000; font-size: 14px; white-space: pre-wrap;">${lineItems}</pre>

<p style="margin: 10px; color: #000000; font-size: 14px;"><strong>Job Total: ${total}</strong></p>

<p style="margin: 10px; color: #000000; font-size: 14px;"><strong>Deadline: ${dueDate}</strong></p>

<p style="margin: 10px; color: #000000; font-size: 14px;">Thanks,</p>

<p style="margin: 10px; color: #000000; font-size: 14px;">${
      outsourcerJob.staff
    }</p>

  </div>
</body>
</html>`;

    return template;
  };

  // Generate email subject
  const getSubject = () => {
    const jobLetter = numToLetter(outsourcerIndex + 1);
    const jobName = getJobName(jobData);

    if (jobLetter === "A") {
      return jobName;
    } else {
      return `${jobName}-${jobLetter}`;
    }
  };

  // Get email recipients
  const getEmailRecipients = () => {
    const toEmails = [{ email: outsourcerData.mainEmail }];
    const ccEmails = outsourcerData.ccEmails?.map((email) => ({ email })) || [];

    return { toEmails, ccEmails };
  };

  const handleSend = async (emailData) => {
    try {
      const result = await sendEmail({
        emailBody: emailData.htmlContent,
        fromEmail: "production@imagesthatsell.com.au",
        toEmails: emailData.to,
        ccEmails: emailData.cc,
        bccEmails: emailData.bcc,
        replyToEmails:
          emailData.replyTo.length > 0
            ? emailData.replyTo
            : ["production@imagesthatsell.com.au"],
        subject: emailData.subject,
        attachments: emailData.attachments || [],
      });

      if (result.data.error) {
        throw new Error(result.data.errortext);
      } else {
        // Update job status
        await updateJobAfterSend();
        onClose();
      }
    } catch (error) {
      console.error("Error sending outsourcer email:", error);
      throw error;
    }
  };

  const updateJobAfterSend = async () => {
    try {
      // Update the specific outsourcer job
      const updatedOutsourcers = [...jobData.outsourcers];
      updatedOutsourcers[outsourcerIndex] = {
        ...outsourcerJob,
        emailSent: true,
        sentDate: DateTime.now().setZone("Australia/Sydney").toUTC().toISO(),
      };

      // Add label for this outsourcer
      let labelToAdd = "";
      labels.forEach((label) => {
        if (
          label.name.toLowerCase() === outsourcerJob.outsourcer.toLowerCase()
        ) {
          labelToAdd = label.id;
        }
      });

      // Update job labels
      const currentLabels = jobData.idLabelsPermanent || [];
      const updatedLabels =
        labelToAdd && !currentLabels.includes(labelToAdd)
          ? [...currentLabels, labelToAdd]
          : currentLabels;
      const uniqueLabels = [...new Set(updatedLabels)];

      // Update the job in Firestore
      await updateDoc(doc(db, "JobData", jobData.id), {
        outsourcers: updatedOutsourcers,
        idLabelsPermanent: uniqueLabels,
      });
    } catch (error) {
      console.error("Error updating job after email send:", error);
      throw error;
    }
  };

  // Update email template when props change
  useEffect(() => {
    setEmailTemplate(generateEmailTemplate());
  }, [jobData, outsourcerJob, outsourcerData, outsourcerIndex]);

  if (!jobData || !outsourcerData || !outsourcerJob) {
    return null;
  }

  const { toEmails, ccEmails } = getEmailRecipients();

  return (
    <EmailModalV2
      open={open}
      onClose={onClose}
      initialTo={toEmails}
      initialCc={ccEmails}
      initialBcc={[]}
      template={emailTemplate}
      subject={getSubject()}
      onSend={handleSend}
      fromEmail="production@imagesthatsell.com.au"
      jobActions={{
        type: "outsourcer-email",
        jobId: jobData.id,
        outsourcerIndex: outsourcerIndex,
        updates: {
          emailSent: true,
          sentDate: DateTime.now().setZone("Australia/Sydney").toUTC().toISO(),
        },
      }}
    />
  );
};

export default OutsourcerEmailV2;
